import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { meApi } from '@/api/me'

/**
 * 当前用户的权限码快照（附录C §2）：按钮级鉴权与菜单同源于服务端的按角色过滤结果。
 *
 * 权限码按应用下发，因此和菜单一样挂在**控制台自身所属应用**上（appStore.consoleClientId）：
 * 按钮属于控制台界面，不会因为你此刻在管哪个业务应用就换一批。
 */
export const usePermStore = defineStore('perm', () => {
  /** 权威权限码：只由服务端快照填充，前端不推断、不补默认值。 */
  const codes = ref<string[]>([])
  /** 服务端权限版本号：与 access_token 中的 permVersion 一致，用于判断是否需要重拉。 */
  const permVersion = ref<string | null>(null)
  /** 当前快照所属应用（client_id）：换应用必须重拉取，否则拿 A 的权限码去判定 B 的按钮。 */
  const loadedClientId = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function load(clientId: string, force = false): Promise<void> {
    if (!force && loadedClientId.value === clientId) {
      return
    }
    loading.value = true
    error.value = null
    try {
      const snapshot = await meApi.permissions(clientId)
      codes.value = snapshot.codes ?? []
      permVersion.value = snapshot.permVersion ?? null
      loadedClientId.value = clientId
    } catch (e) {
      // 拉取失败必须清空：留着上一个应用的权限码，等于把它的按钮继续显示给当前应用
      codes.value = []
      loadedClientId.value = null
      error.value = e instanceof Error ? e.message : '权限加载失败'
    } finally {
      loading.value = false
    }
  }

  function has(code: string): boolean {
    return codes.value.includes(code)
  }

  function hasAny(list: string[]): boolean {
    return list.some((code) => codes.value.includes(code))
  }

  /** 登出清理：与菜单同批重置，避免下一次登录先闪出上一个账号的按钮。 */
  function reset(): void {
    codes.value = []
    permVersion.value = null
    loadedClientId.value = null
    error.value = null
  }

  return {
    codes,
    permVersion,
    loadedClientId,
    loading,
    error,
    load,
    has,
    hasAny,
    reset,
    total: computed(() => codes.value.length),
  }
})
